import { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { Pie, Bar, Line } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, PointElement, LineElement } from 'chart.js';
import { DayPicker } from 'react-day-picker';
import { sk } from 'react-day-picker/locale';
import { format } from 'date-fns';
import { config } from '../config';
import { getAuthToken } from '../utils/authToken';
import { fetchWithRetry } from '../utils/fetchWithRetry';
import FeedbackBox from '../components/FeedbackBox';
import 'react-day-picker/style.css';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, PointElement, LineElement);

type TimeRange = 'this_week' | 'last_7' | 'this_month' | 'last_30' | 'custom';

interface SpendingOverview {
  total_by_currency: { currency: string; total: number }[];
  receipt_count: number;
  avg_basket: number;
  prev_total_by_currency: { currency: string; total: number }[];
  prev_receipt_count: number;
}

interface CategoryBreakdown {
  category: string;
  total: number;
  percentage: number;
}

interface TrendDataPoint {
  month: string;
  currency: string;
  receipt_count: number;
  total: number;
}

type TrendRange = '3' | '6' | '12' | 'all';

interface MerchantData {
  merchant: string;
  total_by_currency: { currency: string; total: number; visit_count: number; avg_basket: number }[];
  total_all: number;
  visit_count: number;
}

interface CategoryTrendSeries {
  category_id: string;
  name: string;
  data: { month: string; total: number }[];
}

interface DrillLevel {
  category_id: string | null;
  name: string;
}

function getDateRange(range: TimeRange, customFrom?: Date | null, customTo?: Date | null): { from: string; to: string; prevFrom: string; prevTo: string } | null {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let from: Date, to: Date, prevFrom: Date, prevTo: Date;

  switch (range) {
    case 'this_week': {
      const day = today.getDay();
      const mondayOffset = day === 0 ? 6 : day - 1;
      from = new Date(today);
      from.setDate(today.getDate() - mondayOffset);
      to = today;
      prevFrom = new Date(from);
      prevFrom.setDate(prevFrom.getDate() - 7);
      prevTo = new Date(from);
      prevTo.setDate(prevTo.getDate() - 1);
      break;
    }
    case 'last_7': {
      from = new Date(today);
      from.setDate(today.getDate() - 6);
      to = today;
      prevFrom = new Date(from);
      prevFrom.setDate(prevFrom.getDate() - 7);
      prevTo = new Date(from);
      prevTo.setDate(prevTo.getDate() - 1);
      break;
    }
    case 'this_month': {
      from = new Date(today.getFullYear(), today.getMonth(), 1);
      to = today;
      prevFrom = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      prevTo = new Date(today.getFullYear(), today.getMonth(), 0);
      break;
    }
    case 'last_30': {
      from = new Date(today);
      from.setDate(today.getDate() - 29);
      to = today;
      prevFrom = new Date(from);
      prevFrom.setDate(prevFrom.getDate() - 30);
      prevTo = new Date(from);
      prevTo.setDate(prevTo.getDate() - 1);
      break;
    }
    case 'custom': {
      if (!customFrom || !customTo) return null;
      from = customFrom;
      to = customTo;
      const diff = to.getTime() - from.getTime();
      prevTo = new Date(from);
      prevTo.setDate(prevTo.getDate() - 1);
      prevFrom = new Date(prevTo.getTime() - diff);
      break;
    }
  }

  const fmt = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  return { from: fmt(from), to: fmt(to), prevFrom: fmt(prevFrom), prevTo: fmt(prevTo) };
}

const chartColors = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40', '#8DD3C7', '#E7E9ED'];

const timeRangeLabels: Record<TimeRange, string> = {
  this_week: 'Tento týždeň',
  last_7: 'Posledných 7 dní',
  this_month: 'Tento mesiac',
  last_30: 'Posledných 30 dní',
  custom: 'Vlastné',
};

const trendRangeLabels: Record<TrendRange, string> = {
  '3': '3 mesiace',
  '6': '6 mesiacov',
  '12': '12 mesiacov',
  'all': 'Všetko',
};

export default function AnalyticsPage() {
  const { instance } = useMsal();
  const [timeRange, setTimeRange] = useState<TimeRange>('last_30');
  const [customFrom, setCustomFrom] = useState<Date | null>(null);
  const [customTo, setCustomTo] = useState<Date | null>(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [overview, setOverview] = useState<SpendingOverview | null>(null);
  const [categoryData, setCategoryData] = useState<Record<number, CategoryBreakdown[]>>({ 1: [], 2: [], 3: [] });
  const [categoryLevel, setCategoryLevel] = useState<1 | 2 | 3>(1);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [trendData, setTrendData] = useState<TrendDataPoint[]>([]);
  const [trendRange, setTrendRange] = useState<TrendRange>('6');
  const [loadingTrend, setLoadingTrend] = useState(true);
  const [merchants, setMerchants] = useState<MerchantData[]>([]);
  const [loadingMerchants, setLoadingMerchants] = useState(true);
  const [categoryTrends, setCategoryTrends] = useState<CategoryTrendSeries[]>([]);
  const [loadingCategoryTrend, setLoadingCategoryTrend] = useState(true);
  const [categoryDrillPath, setCategoryDrillPath] = useState<DrillLevel[]>([{ category_id: null, name: 'Všetky' }]);
  const [showCategoryHint, setShowCategoryHint] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async (range: TimeRange, cFrom?: Date | null, cTo?: Date | null) => {
    const dateRange = getDateRange(range, cFrom, cTo);
    if (!dateRange) return;

    const token = await getAuthToken(instance);
    if (!token) return;

    const { from, to, prevFrom, prevTo } = dateRange;
    setLoadingOverview(true);
    setLoadingCategories(true);
    setLoadingMerchants(true);
    setError(null);

    try {
      const headers = { 'X-Auth-Token': `Bearer ${token}` };
      // Fetch existing reports (critical)
      const [ovRes, cat1Res, cat2Res, cat3Res] = await Promise.all([
        fetchWithRetry(`${config.apiUrl}/getAnalyticsOverview?from=${from}&to=${to}&prevFrom=${prevFrom}&prevTo=${prevTo}`, { headers }),
        fetchWithRetry(`${config.apiUrl}/getAnalyticsCategories?from=${from}&to=${to}&level=1`, { headers }),
        fetchWithRetry(`${config.apiUrl}/getAnalyticsCategories?from=${from}&to=${to}&level=2`, { headers }),
        fetchWithRetry(`${config.apiUrl}/getAnalyticsCategories?from=${from}&to=${to}&level=3`, { headers }),
      ]);

      if (!ovRes.ok || !cat1Res.ok || !cat2Res.ok || !cat3Res.ok) throw new Error('Nepodarilo sa načítať dáta');

      const [ovData, cat1Data, cat2Data, cat3Data] = await Promise.all([
        ovRes.json(), cat1Res.json(), cat2Res.json(), cat3Res.json()
      ]);

      setOverview(ovData);
      setCategoryData({
        1: cat1Data.categories || [],
        2: cat2Data.categories || [],
        3: cat3Data.categories || [],
      });

      // Fetch top merchants (non-critical, don't break page if it fails)
      try {
        const merchantsRes = await fetchWithRetry(`${config.apiUrl}/getAnalyticsTopMerchants?from=${from}&to=${to}`, { headers });
        if (merchantsRes.ok) {
          const merchantsData = await merchantsRes.json();
          setMerchants(merchantsData.merchants || []);
        }
      } catch {
        console.error('Top merchants fetch failed');
      }
    } catch (e: any) {
      setError(e.message || 'Chyba pri načítaní');
    } finally {
      setLoadingOverview(false);
      setLoadingCategories(false);
      setLoadingMerchants(false);
    }
  }, [instance]);

  const fetchTrend = useCallback(async (months: TrendRange) => {
    const token = await getAuthToken(instance);
    if (!token) return;

    setLoadingTrend(true);
    try {
      const headers = { 'X-Auth-Token': `Bearer ${token}` };
      const monthsParam = months === 'all' ? '120' : months;
      const res = await fetchWithRetry(`${config.apiUrl}/getAnalyticsSpendingTrend?months=${monthsParam}`, { headers });
      if (!res.ok) throw new Error('Nepodarilo sa načítať trend');
      const data = await res.json();
      setTrendData(data.trend || []);
    } catch (e: any) {
      // Don't overwrite main error, trend is non-critical
      console.error('Trend fetch failed:', e);
    } finally {
      setLoadingTrend(false);
    }
  }, [instance]);

  useEffect(() => {
    if (timeRange === 'custom') {
      if (customFrom && customTo) fetchData(timeRange, customFrom, customTo);
    } else {
      fetchData(timeRange);
    }
  }, [timeRange, customFrom, customTo, fetchData]);

  useEffect(() => {
    fetchTrend(trendRange);
  }, [trendRange, fetchTrend]);

  const fetchCategoryTrend = useCallback(async (months: TrendRange, categoryId: string | null) => {
    const token = await getAuthToken(instance);
    if (!token) return;

    setLoadingCategoryTrend(true);
    try {
      const headers = { 'X-Auth-Token': `Bearer ${token}` };
      const monthsParam = months === 'all' ? '120' : months;
      const catParam = categoryId ? `&category_id=${encodeURIComponent(categoryId)}` : '';
      const res = await fetchWithRetry(`${config.apiUrl}/getAnalyticsCategoryTrend?months=${monthsParam}${catParam}`, { headers });
      if (!res.ok) throw new Error('Nepodarilo sa načítať trendy kategórií');
      const data = await res.json();
      setCategoryTrends(data.trends || []);
    } catch (e: any) {
      console.error('Category trend fetch failed:', e);
    } finally {
      setLoadingCategoryTrend(false);
    }
  }, [instance]);

  const currentDrillCategory = categoryDrillPath[categoryDrillPath.length - 1];

  useEffect(() => {
    fetchCategoryTrend(trendRange, currentDrillCategory.category_id);
  }, [trendRange, currentDrillCategory.category_id, fetchCategoryTrend]);

  const handleCategoryDrillDown = (categoryId: string, name: string) => {
    setCategoryDrillPath(prev => [...prev, { category_id: categoryId, name }]);
  };

  const handleCategoryBreadcrumb = (index: number) => {
    setCategoryDrillPath(prev => prev.slice(0, index + 1));
  };

  const handleTimeRangeChange = (range: TimeRange) => {
    setTimeRange(range);
    if (range === 'custom') {
      setShowCalendar(true);
    } else {
      setShowCalendar(false);
    }
  };

  const displayCategories = categoryData[categoryLevel] || [];

  const pieChartData = displayCategories.length > 0 ? {
    labels: displayCategories.map(c => c.category),
    datasets: [{
      data: displayCategories.map(c => c.total),
      backgroundColor: chartColors.slice(0, displayCategories.length),
      borderWidth: 2,
      borderColor: '#fff',
    }],
  } : null;

  const formatAmount = (amount: number, currency: string = 'EUR') =>
    `${amount.toFixed(2)} ${currency}`;

  const getTrend = (current: number, previous: number) => {
    if (previous === 0) return null;
    const pct = ((current - previous) / previous) * 100;
    return { pct, direction: pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat' as const };
  };

  return (
    <div style={{ padding: '16px', maxWidth: 720, margin: '0 auto' }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>Analýzy</h2>

      {/* Time range selector */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 6,
        marginBottom: showCalendar ? 8 : 20,
      }}>
        {(Object.keys(timeRangeLabels) as TimeRange[]).map(key => (
          <button
            key={key}
            onClick={() => handleTimeRangeChange(key)}
            style={{
              padding: '8px 6px',
              borderRadius: 20,
              border: timeRange === key ? '2px solid #667eea' : '1px solid #e5e7eb',
              background: timeRange === key ? '#667eea' : '#fff',
              color: timeRange === key ? '#fff' : '#374151',
              fontSize: 12,
              fontWeight: timeRange === key ? 600 : 400,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s ease',
              textAlign: 'center',
            }}
          >
            {timeRangeLabels[key]}
          </button>
        ))}
      </div>

      {/* Custom date picker */}
      {showCalendar && timeRange === 'custom' && (
        <div style={{
          background: '#fff', borderRadius: 14, padding: 16, marginBottom: 20,
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0',
        }}>
          <DayPicker
            mode="range"
            selected={{ from: customFrom || undefined, to: customTo || undefined }}
            onSelect={(range) => {
              setCustomFrom(range?.from || null);
              setCustomTo(range?.to || null);
            }}
            locale={sk}
            footer={
              <div style={{ marginTop: 8, fontSize: 13, color: '#6b7280' }}>
                {customFrom && customTo
                  ? `${format(customFrom, 'dd.MM.yyyy')} - ${format(customTo, 'dd.MM.yyyy')}`
                  : 'Vyberte časové obdobie'}
              </div>
            }
          />
        </div>
      )}

      {error && (
        <div style={{
          background: '#fef2f2', color: '#b91c1c',
          padding: '12px 16px', borderRadius: 10, marginBottom: 16, fontSize: 14
        }}>
          {error}
        </div>
      )}

      {/* Spending Overview (US-003) */}
      <div style={{
        background: '#fff',
        borderRadius: 14,
        padding: 20,
        marginBottom: 16,
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        border: '1px solid #f0f0f0',
      }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 4, color: '#374151' }}>
          Koľko si minul
        </h3>
        <p style={{ fontSize: 12, color: '#9ca3af', marginBottom: 16, marginTop: 0 }}>
          Sumár výdavkov za zvolené obdobie rozdelený podľa meny.
        </p>

        {loadingOverview ? (
          <div style={{ textAlign: 'center', padding: 20, color: '#9ca3af' }}>Načítavam...</div>
        ) : !overview || overview.receipt_count === 0 ? (
          <div style={{ textAlign: 'center', padding: 20, color: '#9ca3af' }}>
            Žiadne bločky za zvolené obdobie
          </div>
        ) : (
          <>
            {/* Total spent per currency */}
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              {overview.total_by_currency.map(({ currency, total }) => {
                const prevEntry = overview.prev_total_by_currency.find(p => p.currency === currency);
                const trend = prevEntry ? getTrend(total, prevEntry.total) : null;
                return (
                  <div key={currency} style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 28, fontWeight: 700, color: '#111827' }}>
                      {formatAmount(total, currency)}
                    </div>
                    {trend && (
                      <div style={{
                        fontSize: 13, marginTop: 4,
                        color: trend.direction === 'down' ? '#059669' : trend.direction === 'up' ? '#dc2626' : '#6b7280',
                      }}>
                        {trend.direction === 'up' ? '+' : ''}{trend.pct.toFixed(1)}% oproti predchádzajúcemu obdobiu
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Stats row */}
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{
                flex: 1, background: '#f9fafb', borderRadius: 10, padding: 14, textAlign: 'center'
              }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#374151' }}>
                  {overview.receipt_count}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                  {overview.receipt_count === 1 ? 'Bloček' : overview.receipt_count < 5 ? 'Bločky' : 'Bločkov'}
                </div>
              </div>
              <div style={{
                flex: 1, background: '#f9fafb', borderRadius: 10, padding: 14, textAlign: 'center'
              }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#374151' }}>
                  {formatAmount(overview.avg_basket)}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Priemer / bloček</div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Category Breakdown (US-004) */}
      <div style={{
        background: '#fff',
        borderRadius: 14,
        padding: 20,
        marginBottom: 16,
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        border: '1px solid #f0f0f0',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: '#374151', margin: 0 }}>
            Rozdelenie výdavkov podľa kategórií
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
            <button
              onClick={() => setCategoryLevel(Math.max(1, categoryLevel - 1) as 1 | 2 | 3)}
              disabled={categoryLevel === 1}
              style={{
                padding: '6px 10px',
                backgroundColor: categoryLevel === 1 ? '#e5e7eb' : '#667eea',
                color: categoryLevel === 1 ? '#9ca3af' : 'white',
                border: 'none',
                borderRadius: 6,
                cursor: categoryLevel === 1 ? 'not-allowed' : 'pointer',
                fontSize: 16
              }}
            >
              &#9664;
            </button>
            <span style={{ fontWeight: 500, minWidth: 80, textAlign: 'center' }}>
              Úroveň {categoryLevel}
            </span>
            <button
              onClick={() => setCategoryLevel(Math.min(3, categoryLevel + 1) as 1 | 2 | 3)}
              disabled={categoryLevel === 3}
              style={{
                padding: '6px 10px',
                backgroundColor: categoryLevel === 3 ? '#e5e7eb' : '#667eea',
                color: categoryLevel === 3 ? '#9ca3af' : 'white',
                border: 'none',
                borderRadius: 6,
                cursor: categoryLevel === 3 ? 'not-allowed' : 'pointer',
                fontSize: 16
              }}
            >
              &#9654;
            </button>
          </div>
        </div>

        {loadingCategories ? (
          <div style={{ textAlign: 'center', padding: 20, color: '#9ca3af' }}>Načítavam...</div>
        ) : displayCategories.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 20, color: '#9ca3af' }}>
            Žiadne kategórie za zvolené obdobie
          </div>
        ) : (
          <>
            {/* Pie chart */}
            {pieChartData && (
              <div style={{ maxWidth: 280, margin: '0 auto 20px' }}>
                <Pie data={pieChartData} />
              </div>
            )}

            {/* Category list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {displayCategories.map((cat, idx) => (
                <div
                  key={cat.category}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 12px', borderRadius: 10,
                    background: '#f9fafb', border: '1px solid #f0f0f0',
                  }}
                >
                  <div style={{
                    width: 12, height: 12, borderRadius: 3, flexShrink: 0,
                    background: chartColors[idx % chartColors.length],
                  }} />
                  <div style={{ flex: 1, fontSize: 14, color: '#374151' }}>
                    {cat.category}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', whiteSpace: 'nowrap' }}>
                    {cat.total.toFixed(2)} EUR
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', minWidth: 40, textAlign: 'right' }}>
                    {cat.percentage.toFixed(1)}%
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Top Merchants (US-006) */}
      <div style={{
        background: '#fff',
        borderRadius: 14,
        padding: 20,
        marginBottom: 16,
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        border: '1px solid #f0f0f0',
      }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 4, color: '#374151' }}>
          Top obchody
        </h3>
        <p style={{ fontSize: 12, color: '#9ca3af', marginBottom: 16, marginTop: 0 }}>
          Kde míňaš najviac za zvolené obdobie.
        </p>

        {loadingMerchants ? (
          <div style={{ textAlign: 'center', padding: 20, color: '#9ca3af' }}>Načítavam...</div>
        ) : merchants.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 20, color: '#9ca3af' }}>
            Žiadne obchody za zvolené obdobie
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {merchants.map((m, idx) => {
              const maxTotal = merchants[0]?.total_all || 1;
              const barWidth = Math.max((m.total_all / maxTotal) * 100, 8);
              return (
                <div
                  key={m.merchant}
                  style={{
                    padding: '12px 14px',
                    borderRadius: 10,
                    background: '#f9fafb',
                    border: '1px solid #f0f0f0',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <div style={{
                      width: 24, height: 24, borderRadius: 6,
                      background: '#667eea', color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, fontWeight: 700, flexShrink: 0,
                    }}>
                      {idx + 1}
                    </div>
                    <div style={{ flex: 1, fontSize: 14, fontWeight: 600, color: '#111827' }}>
                      {m.merchant}
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                      {m.visit_count} {m.visit_count === 1 ? 'návšteva' : m.visit_count < 5 ? 'návštevy' : 'návštev'}
                    </div>
                  </div>
                  {/* Spend bar */}
                  <div style={{ position: 'relative', height: 6, background: '#e5e7eb', borderRadius: 3, marginBottom: 6 }}>
                    <div style={{
                      position: 'absolute', top: 0, left: 0, height: '100%',
                      width: `${barWidth}%`, background: '#667eea', borderRadius: 3,
                    }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span style={{ color: '#374151', fontWeight: 600 }}>
                      {m.total_by_currency.map(c => `${c.total.toFixed(2)} ${c.currency}`).join(' + ')}
                    </span>
                    <span style={{ color: '#6b7280' }}>
                      ~ {m.total_by_currency.map(c => `${c.avg_basket.toFixed(2)} ${c.currency}`).join(' / ')} / návšteva
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Spending Trend (US-005) */}
      <div style={{
        background: '#fff',
        borderRadius: 14,
        padding: 20,
        marginBottom: 16,
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        border: '1px solid #f0f0f0',
      }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 4, color: '#374151' }}>
          Vývoj výdavkov
        </h3>
        <p style={{ fontSize: 12, color: '#9ca3af', marginBottom: 16, marginTop: 0 }}>
          Mesačný prehľad tvojich výdavkov v čase.
        </p>

        {/* Trend time range selector */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 6,
          marginBottom: 16,
        }}>
          {(Object.keys(trendRangeLabels) as TrendRange[]).map(key => (
            <button
              key={key}
              onClick={() => setTrendRange(key)}
              style={{
                padding: '6px 4px',
                borderRadius: 20,
                border: trendRange === key ? '2px solid #667eea' : '1px solid #e5e7eb',
                background: trendRange === key ? '#667eea' : '#fff',
                color: trendRange === key ? '#fff' : '#374151',
                fontSize: 12,
                fontWeight: trendRange === key ? 600 : 400,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s ease',
                textAlign: 'center',
              }}
            >
              {trendRangeLabels[key]}
            </button>
          ))}
        </div>

        {loadingTrend ? (
          <div style={{ textAlign: 'center', padding: 20, color: '#9ca3af' }}>Načítavam...</div>
        ) : trendData.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 20, color: '#9ca3af' }}>
            Žiadne dáta za zvolené obdobie
          </div>
        ) : (() => {
          // Group trend data by currency
          const currencies = [...new Set(trendData.map(d => d.currency))];
          const months = [...new Set(trendData.map(d => d.month))].sort();
          const currencyColors: Record<string, string> = { EUR: '#667eea', CZK: '#f59e0b', PLN: '#10b981', USD: '#ef4444', GBP: '#8b5cf6' };

          const barData = {
            labels: months.map(m => {
              const [y, mo] = m.split('-');
              const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'Máj', 'Jún', 'Júl', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];
              return `${monthNames[parseInt(mo, 10) - 1]} ${y.slice(2)}`;
            }),
            datasets: currencies.map(currency => ({
              label: currency,
              data: months.map(m => {
                const point = trendData.find(d => d.month === m && d.currency === currency);
                return point ? point.total : 0;
              }),
              backgroundColor: currencyColors[currency] || '#667eea',
              borderRadius: 4,
            })),
          };

          return (
            <Bar
              data={barData}
              options={{
                responsive: true,
                plugins: {
                  legend: { display: currencies.length > 1, position: 'top' as const },
                  tooltip: {
                    callbacks: {
                      label: (ctx) => `${ctx.parsed.y.toFixed(2)} ${ctx.dataset.label}`,
                    }
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: { callback: (v) => `${v} €` },
                  },
                  x: {
                    grid: { display: false },
                  },
                },
              }}
            />
          );
        })()}
      </div>

      {/* Category Trends (US-007) */}
      <div style={{
        background: '#fff',
        borderRadius: 14,
        padding: 20,
        marginBottom: 16,
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        border: '1px solid #f0f0f0',
      }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 4, color: '#374151' }}>
          Trendy kategórií
        </h3>
        <p style={{ fontSize: 12, color: '#9ca3af', marginBottom: 8, marginTop: 0 }}>
          Tu nájdeš ako sa vyvíjajú tvoje výdavky podľa jednotlivých kategórií.
        </p>
        <button
          onClick={() => setShowCategoryHint(prev => !prev)}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            marginBottom: showCategoryHint ? 6 : 12,
            cursor: 'pointer',
            fontSize: 12,
            color: '#667eea',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <span style={{ fontSize: 10, transition: 'transform 0.2s', display: 'inline-block', transform: showCategoryHint ? 'rotate(90deg)' : 'rotate(0deg)' }}>&#9654;</span>
          Nápoveda
        </button>
        {showCategoryHint && (
          <div style={{
            fontSize: 11, color: '#6b7280', lineHeight: 1.6,
            background: '#f9fafb', borderRadius: 8, padding: '10px 12px',
            marginBottom: 12, border: '1px solid #f0f0f0',
          }}>
            Kliknutím na kategóriu pod grafom otvoríš jej detail a uvidíš vývoj podkategórií. Aj tie podkategórie sa dajú ešte ďalej otvoriť. Ak otvoríš detail nejakej kategórie, nad grafom sa zobrazí textová linka, cez ktorú sa vieš vrátiť naspäť.
          </div>
        )}

        {/* Breadcrumb */}
        {categoryDrillPath.length > 1 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12, fontSize: 13 }}>
            {categoryDrillPath.map((level, idx) => (
              <span key={idx}>
                {idx > 0 && <span style={{ color: '#9ca3af', margin: '0 2px' }}> &gt; </span>}
                <button
                  onClick={() => handleCategoryBreadcrumb(idx)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: idx < categoryDrillPath.length - 1 ? 'pointer' : 'default',
                    color: idx < categoryDrillPath.length - 1 ? '#667eea' : '#374151',
                    fontWeight: idx === categoryDrillPath.length - 1 ? 600 : 400,
                    textDecoration: idx < categoryDrillPath.length - 1 ? 'underline' : 'none',
                    fontSize: 13,
                  }}
                >
                  {level.name}
                </button>
              </span>
            ))}
          </div>
        )}

        {loadingCategoryTrend ? (
          <div style={{ textAlign: 'center', padding: 20, color: '#9ca3af' }}>Načítavam...</div>
        ) : categoryTrends.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 20, color: '#9ca3af' }}>
            Žiadne dáta za zvolené obdobie
          </div>
        ) : (() => {
          const allMonths = [...new Set(categoryTrends.flatMap(s => s.data.map(d => d.month)))].sort();
          const monthLabels = allMonths.map(m => {
            const [y, mo] = m.split('-');
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'Máj', 'Jún', 'Júl', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];
            return `${monthNames[parseInt(mo, 10) - 1]} ${y.slice(2)}`;
          });

          const lineColors = ['#667eea', '#ef4444', '#f59e0b', '#10b981', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'];

          const lineData = {
            labels: monthLabels,
            datasets: categoryTrends.map((series, idx) => ({
              label: series.name,
              data: allMonths.map(m => {
                const point = series.data.find(d => d.month === m);
                return point ? point.total : 0;
              }),
              borderColor: lineColors[idx % lineColors.length],
              backgroundColor: lineColors[idx % lineColors.length] + '20',
              tension: 0.3,
              pointRadius: 3,
              pointHoverRadius: 6,
              borderWidth: 2,
            })),
          };

          return (
            <>
              <div style={{ position: 'relative', width: '100%', minHeight: '300px' }}>
              <Line
                data={lineData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  interaction: { mode: 'index' as const, intersect: false },
                  plugins: {
                    legend: {
                      display: true,
                      position: 'bottom' as const,
                      labels: { usePointStyle: true, boxWidth: 8, padding: 12, font: { size: 11 } },
                      onClick: (_e, legendItem, legend) => {
                        const index = legendItem.datasetIndex!;
                        const ci = legend.chart;
                        const meta = ci.getDatasetMeta(index);
                        meta.hidden = meta.hidden === null ? !ci.data.datasets[index].hidden : null;
                        ci.update();
                      },
                    },
                    tooltip: {
                      callbacks: {
                        label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(2)} EUR`,
                      }
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: { callback: (v) => `${v} €` },
                    },
                    x: {
                      grid: { display: false },
                    },
                  },
                }}
              />
              </div>
              {/* Drill-down list */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 16 }}>
                {categoryTrends.map((series, idx) => {
                  const seriesTotal = series.data.reduce((sum, d) => sum + d.total, 0);
                  return (
                    <button
                      key={series.category_id}
                      onClick={() => handleCategoryDrillDown(series.category_id, series.name)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '10px 12px',
                        borderRadius: 10,
                        background: '#f9fafb',
                        border: '1px solid #f0f0f0',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'background 0.15s',
                        width: '100%',
                      }}
                    >
                      <div style={{
                        width: 12, height: 12, borderRadius: 3, flexShrink: 0,
                        background: lineColors[idx % lineColors.length],
                      }} />
                      <div style={{ flex: 1, fontSize: 14, color: '#374151' }}>
                        {series.name}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', whiteSpace: 'nowrap' }}>
                        {seriesTotal.toFixed(2)} EUR
                      </div>
                      <div style={{ fontSize: 14, color: '#9ca3af' }}>›</div>
                    </button>
                  );
                })}
              </div>
            </>
          );
        })()}
      </div>

      {/* Feedback Box */}
      <div style={{ marginBottom: 80 }}>
        <FeedbackBox pageContext="analytics" />
      </div>
    </div>
  );
}
